<template>
  <SheetModal
    class="ReviewRunModalView"
    data-theme="light"
    aria-label="Review the run"
    :aria-busy="isRequesting || isSaving"
    @close="handleClose"
  >
    <form
      class="ReviewRunModalView__form"
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="ReviewRunModalView__fieldset"
        :disabled="isRequesting || isSaving"
      >
        <SheetHeader
          title="Review the run"
          @cancel="handleClose"
        />
        <Transition>
          <div
            v-if="!isRequesting && !!run"
            class="ReviewRunModalView__content"
          >
            <RunHeroView
              v-model="run"
              class="ReviewRunModalView__hero"
              theme="light"
            />
            <div class="ReviewRunModalView__body">
              <FormField
                :label="`Vote for the ${mvpAward?.award.name}`"
                isFieldset
              >
                <PlayerSelectList
                  v-model="mvpRecipientId"
                  :players="attendingPlayers"
                  isRequired
                />
              </FormField>
            </div>
          </div>
        </Transition>
      </fieldset>
    </form>
  </SheetModal>
</template>

<script lang="ts" setup>
import FormField from "@/components/FormField.vue";
import PlayerSelectList from "@/components/PlayerSelectList.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import { supabaseClient } from "@/lib/database/supabase.ts";
import isTruthy from "@/lib/utils/boolean/isTruthy.ts";
import useAuthStore from "@/stores/useAuthStore";
import useModalStore from "@/stores/useModalStore";
import useRunReviewStore from "@/stores/useRunReviewStore.ts";
import { CourtSummary, CrewSummary, RunAwardWithAward, RunReviewWithVotes, RunWithStatusPlayers } from "@shared/lib/database/query.types.ts";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";
import RunHeroView from "../RunHeroView.vue";

const {
  runId,
} = defineProps<{
  runId: string,
}>();

const {
  closeActiveModal,
} = useModalStore();

const {
  fetchReviewForRun,
  getReviewForRun,
} = useRunReviewStore();

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const isRequesting = ref(true);
const isSaving = ref(false);
const review = ref<RunReviewWithVotes | null>(getReviewForRun(runId));
const mvpRecipientId = ref<string | null>(getMvpRecipientId());

const run = ref<RunWithStatusPlayers & {
  crews: Array<CrewSummary>,
  court: CourtSummary | null,
  awards: Array<RunAwardWithAward>,
}>();

onBeforeMount(async () => {
  const {
    data: _run,
    error,
  } = await supabaseClient.from("runs")
    .select(`*, 
      statuses(*, player:players(*)), 
      crews(*, players(*)), 
      court:courts(*), 
      awards:run_awards(*, award:awards(*))
    `)
    .eq("id", runId)
    .limit(1)
    .maybeSingle();

  if (error) {
    alert(error.message);
    closeActiveModal();
    return;
  } else if (!_run) {
    alert("This run doesn’t exist");
    closeActiveModal();
    return;
  } else if (_run.awards.length === 0) {
    alert("This run has no awards");
    closeActiveModal();
    return;
  }

  run.value = _run;

  try {
    review.value = await fetchReviewForRun(runId) ?? null;
    mvpRecipientId.value = getMvpRecipientId();
  } catch (error) {
    alert((error as Error).message);
  }

  isRequesting.value = false;
});

const attendingPlayers = computed(() => {
  return run.value?.statuses
    .filter((status) => status.probability > 0 && status.player_id !== currentPlayer.value?.id)
    .map((status) => status.player)
    .filter(isTruthy) ?? [];
});

const mvpAward = computed(() => {
  return run.value?.awards.find(({
    award,
  }) => award.name === "MVP");
});

function getMvpRecipientId () {
  return review.value?.award_votes.at(0)?.recipient_id ?? null;
}

async function handleSubmit () {
  if (!run.value?.id) return;

  isSaving.value = true;

  const {
    data: _review,
    error,
  } = await supabaseClient.rpc("upsert_run_review", {
    run_id: run.value.id,
    votes: [
      {
        award_id: mvpAward.value?.award_id,
        recipient_id: mvpRecipientId.value,
      },
    ],
  });

  if (error) {
    alert(error.message);
  } else if (_review) {
    try {
      fetchReviewForRun(runId);
    } catch (_) {
      // noop
    }
    closeActiveModal();
  }

  isSaving.value = false;
}

function handleClose () {
  closeActiveModal();
}
</script>

<style lang="scss" scoped>
.ReviewRunModalView__form {
  position: absolute;
  inset: 0;
}

.ReviewRunModalView__fieldset {
  display: grid;
  grid-template-rows: min-content auto;
  height: 100%;
}

.ReviewRunModalView__content {
  display: grid;
  grid-template-rows: min-content auto;
  min-height: 0;
  overflow-y: auto;

  transition: opacity 350ms;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.ReviewRunModalView__hero {
  border-bottom: 1px solid var(--themeColorNeutral100);
}

.ReviewRunModalView__body {
  padding: 20px 16px calc(20px + var(--safeAreaInsetBottom));
}

.ReviewRunModalView__bodyTitle {
  color: var(--themeColorNeutral600);
  font: var(--fontWeightMedium) 14px / 1 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
}

.ReviewRunModalView__playerListContainer {
  display: grid;
  min-height: 0;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 8px;
  background-color: var(--themeColorWhite);
}

.ReviewRunModalView__playerList {
  position: relative;

  display: grid;
  gap: 1px;
  overflow-y: auto;

  border-radius: 6px;
}

.ReviewRunModalView__playerListItem {
  --transitionDuration: 250ms;
  transition: var(--transitionDuration);
  transition-property: filter, opacity, background-color, outline-color;

  &[aria-selected="true"] {
    background-color: var(--colorGold);
  }

  &:has(+ [aria-selected="true"]),
  &[aria-selected="true"],
  &[aria-selected="true"] + & {
    outline-color: transparent;
  }

  .ReviewRunModalView__playerList:has([aria-selected="true"]) &[aria-selected="false"] {
    opacity: 0.5;
    filter: grayscale(100%);
  }
}

.ReviewRunModalView__playerItemIconContainer {
  display: grid;
  place-items: center;
  height: 100%;
  aspect-ratio: 1;
  margin-right: 4px;

  transition: opacity var(--transitionDuration);

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.ReviewRunModalView__playerItemIcon {
  position: relative;
  top: 2px;
}
</style>
